import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase/firebase";

import { resolveSubtopicBand } from "./resolveSubtopicBand";
import { ENGLISH_SUBTOPICS } from "../../data/skillTaxonomy";
import { ENGLISH_PILLARS } from "../../data/pillars";

import { estimatePillarScore } from "../analytics/estimatePillarScore";
import { rawToScaledScore } from "../analytics/rawToScaledScore";
import { saveAnalyticsResults } from "../analytics/saveAnalyticsResults";

/**
 * Submits a completed diagnostic and assigns placement bands,
 * then recomputes and saves pillar + overall score estimates.
 */
export async function submitEnglishDiagnostic({ userUid, diagnosticAttempt }) {
  if (!userUid || !diagnosticAttempt) {
    throw new Error("submitEnglishDiagnostic: missing arguments");
  }

  const { responses, sequence } = diagnosticAttempt;

  /* ======================================================
     1. Resolve bands per subtopic → per skill
     ====================================================== */

  const analyticsSkills = {};
  const subtopicAnswers = {};

  sequence.forEach((qid) => {
    const match = qid.match(/^eng_diag_([a-z]+)_([123])_[ab]$/);
    if (!match) return;

    const [, subtopic, level] = match;

    if (!subtopicAnswers[subtopic]) {
      subtopicAnswers[subtopic] = [];
    }

    subtopicAnswers[subtopic].push({
      level,
      correct: Boolean(responses[qid]?.correct),
    });
  });

  Object.entries(subtopicAnswers).forEach(([subtopic, answers]) => {
    if (answers.length !== 3) return;

    const band = resolveSubtopicBand(answers);
    const skills = ENGLISH_SUBTOPICS[subtopic]?.skills;

    if (!skills) {
      console.warn(`Unknown subtopic code: ${subtopic}`);
      return;
    }

    skills.forEach((skillCode) => {
      analyticsSkills[skillCode] = {
        band,
        attempted: 0,
        accuracy: null,
        bandProgress: 0,
        canPromote: false,
        lastCompletedAt: serverTimestamp(),
      };
    });
  });

  /* ======================================================
     2. Write analytics.skills (canonical state)
     ====================================================== */

  const userRef = doc(db, "users", userUid);

  await updateDoc(userRef, {
    "analytics.english.skills": analyticsSkills,
    "analytics.english.updatedAt": serverTimestamp(),
  });

  /* ======================================================
     3. Recompute pillar estimates (English)
     ====================================================== */

  const englishSkillBands = Object.fromEntries(
    Object.entries(analyticsSkills).map(([k, v]) => [k, v.band])
  );

  const englishPillars = ENGLISH_PILLARS.map((pillar) => {
    const rows = estimatePillarScore({
      pillar,
      skillBandMap: englishSkillBands,
      totalQuestions: 66,
    });

    const correctLow = rows.reduce((s, r) => s + (r.correctLow || 0), 0);
    const correctEst = rows.reduce((s, r) => s + (r.correctEst || 0), 0);
    const correctHigh = rows.reduce((s, r) => s + (r.correctHigh || 0), 0);

    return {
      id: pillar.id,
      low: rawToScaledScore(correctLow, "reading_writing", "lower"),
      est: rawToScaledScore(correctEst, "reading_writing", "middle"),
      high: rawToScaledScore(correctHigh, "reading_writing", "upper"),
    };
  });

  /* ======================================================
     4. Aggregate overall English using pillarWeight
     ====================================================== */

  const englishOverall = ENGLISH_PILLARS.reduce(
    (acc, pillar) => {
      const p = englishPillars.find((x) => x.id === pillar.id);
      if (!p) return acc;

      acc.low += p.low * pillar.pillarWeight;
      acc.est += p.est * pillar.pillarWeight;
      acc.high += p.high * pillar.pillarWeight;

      return acc;
    },
    { low: 0, est: 0, high: 0 }
  );

  // Round to nearest 10 (SAT scores)
  Object.keys(englishOverall).forEach((k) => {
    englishOverall[k] = Math.round(englishOverall[k] / 10) * 10;
  });

  /* ======================================================
     5. Persist results
     ====================================================== */

  await saveAnalyticsResults({
    uid: userUid,

    // overwrite current pillar state
    pillarEstimates: {
      english: Object.fromEntries(
        englishPillars.map((p) => [p.id, p])
      ),
    },

    // append / override daily overall history
    overallEstimate: {
      english: englishOverall,
    },

    source: "diagnostic",
  });
}
